'use client';
import MuiTextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import InfoIcon from '../../icons/Info';
import StatusExclamationIcon from '../../icons/StatusExclamation';
import idsTokensDefault from '../../utils/tokens/idsTokens';
import withRef from '../../utils/withRef';
import IconButton from '../IconButton';
import InputAdornment from '../InputAdornment';
import Tooltip from '../Tooltip';

export const styles = ({ idsTokens = idsTokensDefault }) => ({
  optional: {
    marginLeft: idsTokens.label.default.spacing.padding.gap,
  },
  asterisk: {
    '& .MuiFormLabel-asterisk': {
      fontSize: idsTokens.typography.font.size.base,
      lineHeight: idsTokens.typography.lineHeight.base,
      fontWeight: idsTokens.typography.font.weight.bold,
      top: -3,
      left: 'initial',
      marginLeft: 0,
      color: idsTokens.label.default.color.asterisk,
    },
  },
  asteriskLeftLabel: {
    '& .MuiFormLabel-asterisk': {
      marginLeft: 'unset',
      position: 'absolute',
      left: -15,
    },
  },
  asteriskLeft: {
    paddingLeft: 12,
    '& label': {
      height: 0,
    },
    '& label span': {
      top: 4,
      left: -11,
    },
  },
  startIcon: {
    '& svg': {
      fontSize: 19.22,
      padding: 0.4,
      boxSizing: 'content-box',
    },
  },
  smallStartIcon: {
    '& svg': {
      fontSize: 16.8,
      padding: 1.1,
      boxSizing: 'content-box',
    },
  },
  inputWithStartIcon: {
    '& .MuiInputBase-input, & .MuiInput-input[aria-invalid="true"]': {
      paddingLeft: 31,
    },
  },
  inputWithStartIconSmall: {
    '& .MuiInput-input.MuiInputBase-inputSizeSmall': {
      paddingLeft: 29,
    },
  },
  noMargin: {
    '& > div': {
      margin: 0,
    },
  },
});

const useStyles = makeStyles()(styles);

export const TextField = ({
  InputLabelProps,
  InputProps,
  inputProps,
  iconProps,
  multiline,
  required,
  optional,
  optionalText = '(optional)',
  minRows = 4,
  sizeAdjustable = false,
  minWidth = 112,
  minHeight = 56,
  maxWidth,
  maxHeight,
  label,
  icon,
  IconButtonProps,
  tooltipProps,
  disabled,
  className,
  size,
  startIcon,
  margin = 'normal',
  asteriskPosition,
  variant = 'standard',
  onClick,
  onChange,
  forwardedRef: ref,
  iconPosition = 'start',
  helperText,
  showHelperTextIcon,
  characterLimit,
  value,
  error,
  slots,
  slotProps,
  ...rest
}) => {
  const { classes, cx } = useStyles();

  const [count, setCount] = React.useState(value?.length || 0);

  const handleControlChange = (value) => {
    setCount(value?.length || 0);
  };

  const characterCount = value?.length || count;

  if (minRows < 2) {
    minRows = 2;
  }

  return (
    <MuiTextField
      slotProps={{
        input: {
          disableUnderline: true,
          endAdornment: icon && (
            <InputAdornment position="end" {...iconProps}>
              {tooltipProps ? (
                <Tooltip placement="top" key={tooltipProps.title} {...tooltipProps}>
                  <IconButton size="small" {...IconButtonProps}>
                    {icon}
                  </IconButton>
                </Tooltip>
              ) : (
                <IconButton size="small" {...IconButtonProps}>
                  {icon}
                </IconButton>
              )}
            </InputAdornment>
          ),
          startAdornment: startIcon && (
            <InputAdornment
              position={iconPosition}
              className={size === 'small' ? classes.smallStartIcon : classes.startIcon}
            >
              {startIcon}
            </InputAdornment>
          ),
          ...InputProps,
          ...slotProps?.input,
        },
        htmlInput: {
          ...InputProps?.inputProps,
          ...inputProps,
          ...slotProps?.htmlInput,
          style: {
            resize: sizeAdjustable ? 'both' : 'none',
            minWidth: sizeAdjustable ? minWidth : undefined,
            minHeight: sizeAdjustable ? minHeight - 18 : undefined,
            maxWidth: sizeAdjustable ? maxWidth : undefined,
            maxHeight: sizeAdjustable ? maxHeight : undefined,
            overflow: sizeAdjustable ? 'auto' : undefined,
            ...InputProps?.inputProps?.style,
            ...inputProps?.style,
            ...slotProps?.htmlInput?.style,
          },
        },
        inputLabel: {
          shrink: true,
          ...InputLabelProps,
          ...slotProps?.inputLabel,
        },
      }}
      className={cx(
        required && [
          classes.asterisk,
          asteriskPosition === 'before' &&
            (label ? classes.asteriskLeftLabel : classes.asteriskLeft),
        ],
        margin === 'none' && !label && classes.noMargin,
        startIcon &&
          (size === 'small' ? classes.inputWithStartIconSmall : classes.inputWithStartIcon),
        className
      )}
      margin={margin}
      multiline={multiline ?? sizeAdjustable}
      minRows={multiline && minRows}
      label={
        label || optional || required ? (
          <>
            {label}
            {optional && !required && <span className={classes.optional}>{optionalText}</span>}
          </>
        ) : (
          undefined
        )
      }
      required={required}
      disabled={disabled}
      size={size}
      variant={variant}
      helperText={
        <>
          {showHelperTextIcon ? (
            <>
              {error ? (
                <StatusExclamationIcon fontSize="extraSmall" />
              ) : (
                <InfoIcon fontSize="extraSmall" />
              )}
              {helperText}
            </>
          ) : (
            helperText
          )}
          {(multiline || sizeAdjustable) && characterLimit && (
            <span>{`${characterCount}/${characterLimit}`}</span>
          )}
        </>
      }
      onChange={(event, ...rest) => {
        if (value === undefined && (multiline || sizeAdjustable) && characterLimit) {
          handleControlChange(event.target.value);
        }
        onChange && onChange(event, ...rest);
      }}
      onClick={(event, ...rest) => {
        /**
         * if we are in firefox and the input has type="number" the
         * focus event is not triggered when clicking on its
         * up/down arrows so we do it manually
         */
        if (event.target.type === 'number') {
          event.target.focus();
        }
        onClick && onClick(event, ...rest);
      }}
      value={value}
      error={error}
      {...rest}
      ref={ref}
    />
  );
};

TextField.propTypes = {
  /** Position of the asterisk when `required` is `true`. */
  asteriskPosition: PropTypes.oneOf(['before', 'after']),
  /** If `true`, the input element is focused during the first mount. */
  autoFocus: PropTypes.bool,
  /**
   * Adds a character counter with the given number as the character limit.
   * It is only available if multiline or sizeAdjustable is `true`.
   */
  characterLimit: PropTypes.number,
  /** The default value. Use when the component is not controlled. */
  defaultValue: PropTypes.any,
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** If `true`, the label is displayed in an error state. */
  error: PropTypes.bool,
  /** If `true`, the component takes up the full width of its container. */
  fullWidth: PropTypes.bool,
  /** The helper text content. */
  helperText: PropTypes.node,
  /** Add an icon to the right on the inside of the input box. */
  icon: PropTypes.element,
  /** The position of the icon, if there is one. */
  iconPosition: PropTypes.oneOf(['start', 'end']),
  /** Props applied to the icon element. */
  iconProps: PropTypes.shape({
    /** Callback fired when the component is clicked. */
    onClick: PropTypes.func,
  }),
  /**
   * @deprecated use slotProps.inputLabel instead
   *
   * Props applied to the `InputLabel` component.
   **/
  InputLabelProps: PropTypes.object,

  /**
   * @deprecated use slotProps.htmlInput instead
   *
   * Attributes applied to the `input` element.
   **/
  inputProps: PropTypes.object,

  /**
   * @deprecated use slotProps.input instead
   *
   * Props applied to the `Input` component.
   **/
  InputProps: PropTypes.object,
  /** The label content. */
  label: PropTypes.node,
  /**
   * If `dense` or `normal`, the vertical spacing of this and contained
   * components is adjusted.
   */
  margin: PropTypes.oneOf(['dense', 'none', 'normal']),
  /** Maximum height of the size adjustable `TextField`. */
  maxHeight: PropTypes.number,
  /** Maximum width of the size adjustable `TextField`. */
  maxWidth: PropTypes.number,
  /** Minimum height of the size adjustable `TextField`. */
  minHeight: PropTypes.number,
  /** Minimum number of rows to display when multiline option is set to `true`. */
  minRows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Minimum width of the size adjustable `TextField`. */
  minWidth: PropTypes.number,
  /** If `true`, a textarea element is rendered instead of an input. */
  multiline: PropTypes.bool,
  /** Callback fired when the value changes. */
  onChange: PropTypes.func,
  /** If `true`, the label is displayed as optional. */
  optional: PropTypes.bool,
  /** Custom text for the optional label (displayed next to the main label). */
  optionalText: PropTypes.string,
  /** The short hint displayed in the input before the user enters a value. */
  placeholder: PropTypes.string,
  /** If `true`, the label is displayed as required. */
  required: PropTypes.bool,
  /** If `true`, a leading icon is added to the helper text. */
  showHelperTextIcon: PropTypes.bool,
  /** The size of the component. */
  size: PropTypes.oneOf(['small', 'medium']),
  /**
   * If `true`, allows user to adjust the text box size.
   * Used with minWidth and minHeight.
   */
  sizeAdjustable: PropTypes.bool,
  /** Add an icon to the left on the inside of the input box. */
  startIcon: PropTypes.element,
  /** Props applied to the `Tooltip` for the icon, if used. */
  tooltipProps: PropTypes.object,
  /** The value of the input element, required for a controlled component. */
  value: PropTypes.any,
};

export default withRef()(TextField);
